import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Lock, Send, Unplug, Mail, MessageCircle, AlertTriangle, CheckCircle2 } from 'lucide-react';
import api from '../../api/axios';

// Admin → Notifications (admins and superadmins):
//   1. personal messages: Telegram on/off and email off / fallback / always per type
//   2. the Telegram group moderator (shift-exchange topic)

const EMAIL_OPTIONS = [
  { value: 'off', label: 'Off' },
  { value: 'fallback', label: 'Only without Telegram' },
  { value: 'always', label: 'Always' },
];
const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const sameRule = (a, b) => a.telegram === b.telegram && a.email === b.email;

function StatusDot({ ok, children }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '0.85rem', color: ok ? 'var(--success)' : 'var(--text-muted)' }}>
      <span style={{ width: 9, height: 9, borderRadius: '50%', background: ok ? 'var(--success)' : 'var(--text-muted)', flexShrink: 0 }} />
      {children}
    </span>
  );
}

function Toggle({ checked, onChange, label, disabled }) {
  return (
    <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', cursor: disabled ? 'default' : 'pointer', minHeight: '36px', fontSize: '0.85rem' }}>
      <input type="checkbox" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} style={{ width: 18, height: 18 }} />
      {label}
    </label>
  );
}

// ── Personal messages ─────────────────────────────────────────────────────────
function ChannelsCard({ data, onSaved }) {
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    setDraft(Object.fromEntries(data.types.map((t) => [t.key, { ...t.rule }])));
  }, [data]);

  const changed = data.types.filter((t) => draft[t.key] && !sameRule(draft[t.key], t.rule));
  const set = (key, patch) => setDraft((d) => ({ ...d, [key]: { ...d[key], ...patch } }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/notification-settings/channels', {
        channels: Object.fromEntries(changed.map((t) => [t.key, draft[t.key]])),
      });
      toast.success('Notification settings saved.');
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  const testEmail = async () => {
    setTesting(true);
    try {
      const r = await api.post('/notification-settings/test-email');
      toast.success(`Test email sent to ${r.data.email}.`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to send the test email.');
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="card" style={{ padding: '20px' }}>
      <h3 style={{ margin: '0 0 4px', fontSize: '1rem' }}>Notifications</h3>
      <p style={{ margin: '0 0 12px', fontSize: '0.85rem', color: 'var(--text-light)' }}>
        Which channel each personal message uses. In-app notifications (the bell) are always shown.
        &quot;Only without Telegram&quot; = email for people who have not linked Telegram, or when Telegram fails.
      </p>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px 16px', marginBottom: '14px' }}>
        <StatusDot ok={data.bot_active}>{data.bot_active ? `Telegram bot active${data.bot_username ? ` (@${data.bot_username})` : ''}` : 'Telegram bot not running'}</StatusDot>
        <StatusDot ok={data.smtp_configured}>{data.smtp_configured ? 'Email (SMTP) configured' : 'Email (SMTP) not configured'}</StatusDot>
        <button className="btn btn-secondary btn-sm" style={{ minHeight: '36px' }} onClick={testEmail} disabled={testing || !data.smtp_configured}>
          <Mail size={14} /> {testing ? 'Sending…' : 'Send test email'}
        </button>
      </div>
      {data.notifications_disabled && (
        <p style={{ margin: '0 0 12px', padding: '8px 10px', borderRadius: 'var(--radius)', background: 'var(--warning-light, #FFF8E1)', fontSize: '0.82rem' }}>
          This server runs with NOTIFICATIONS_DISABLED — nothing is actually sent (development / tests).
        </p>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', borderTop: '1px solid var(--border)' }}>
        {data.types.map((t) => {
          const d = draft[t.key] || t.rule;
          const isChanged = !sameRule(d, t.rule);
          const isDefault = sameRule(d, t.default);
          return (
            <div key={t.key} style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '4px 14px', padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
              <div style={{ flex: '1 1 240px', minWidth: 0 }}>
                <div style={{ fontSize: '0.88rem', fontWeight: 600 }}>
                  {t.label}
                  {isChanged && <span className="badge badge-warning" style={{ marginLeft: '6px', fontSize: '0.68rem' }}>Changed</span>}
                </div>
                <div style={{ fontSize: '0.76rem', color: 'var(--text-muted)' }}>
                  To: {t.recipients}{!isDefault && !isChanged ? ' · differs from the default' : ''}
                </div>
              </div>
              <Toggle checked={d.telegram} onChange={(v) => set(t.key, { telegram: v })} label={<><MessageCircle size={14} /> Telegram</>} />
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '0.85rem' }}>
                <Mail size={14} aria-hidden="true" />
                <select aria-label={`Email for ${t.label}`} className="form-select" value={d.email} onChange={(e) => set(t.key, { email: e.target.value })}
                  style={{ minHeight: '36px', padding: '4px 8px', width: 'auto', fontSize: '0.85rem' }}>
                  {EMAIL_OPTIONS.map((o) => <option key={o.value} value={o.value}>Email: {o.label}</option>)}
                </select>
              </label>
            </div>
          );
        })}
        {data.locked.map((t) => (
          <div key={t.key} style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '4px 14px', padding: '8px 0', borderBottom: '1px solid var(--border)', color: 'var(--text-light)' }}>
            <div style={{ flex: '1 1 240px', minWidth: 0 }}>
              <div style={{ fontSize: '0.88rem', fontWeight: 600 }}>{t.label}</div>
              <div style={{ fontSize: '0.76rem', color: 'var(--text-muted)' }}>To: {t.recipients} · needed to log in</div>
            </div>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '0.85rem' }}>
              <Lock size={14} aria-hidden="true" /> Email: always on
            </span>
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', gap: '8px', marginTop: '14px', flexWrap: 'wrap' }}>
        <button className="btn btn-primary btn-sm" style={{ minHeight: '40px' }} onClick={save} disabled={saving || !changed.length}>
          {saving ? 'Saving…' : changed.length ? `Save (${changed.length} changed)` : 'Save'}
        </button>
        {changed.length > 0 && (
          <button className="btn btn-secondary btn-sm" style={{ minHeight: '40px' }} disabled={saving}
            onClick={() => setDraft(Object.fromEntries(data.types.map((t) => [t.key, { ...t.rule }])))}>
            Cancel
          </button>
        )}
      </div>
    </div>
  );
}

// ── Telegram group moderator ──────────────────────────────────────────────────
function GroupCard({ data, onSaved }) {
  const g = data.group;
  const [posts, setPosts] = useState(g.posts);
  const [busy, setBusy] = useState('');

  useEffect(() => { setPosts(g.posts); }, [g.posts]);
  const changed = JSON.stringify(posts) !== JSON.stringify(g.posts);
  const set = (key, patch) => setPosts((p) => ({ ...p, [key]: { ...p[key], ...patch } }));

  const run = async (what, fn, ok) => {
    setBusy(what);
    try {
      await fn();
      if (ok) toast.success(ok);
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Something went wrong.');
    } finally {
      setBusy('');
    }
  };

  const fmt = (iso) => (iso ? new Date(iso).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '');

  return (
    <div className="card" style={{ padding: '20px' }}>
      <h3 style={{ margin: '0 0 4px', fontSize: '1rem' }}>Telegram group: shift exchange</h3>
      <p style={{ margin: '0 0 12px', fontSize: '0.85rem', color: 'var(--text-light)' }}>
        The bot posts open shifts into one topic of the staff group and edits its posts when a shift is taken.
        Posts never mention sickness, reasons, hours or pay. Taking a shift always happens in the app.
      </p>

      {g.group ? (
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px 12px', marginBottom: '12px' }}>
          <StatusDot ok>
            Connected: {g.group.chat_title || 'group'} → {g.group.topic_name ? `“${g.group.topic_name}”` : `topic #${g.group.thread_id}`}
          </StatusDot>
          <button className="btn btn-secondary btn-sm" style={{ minHeight: '36px' }} disabled={!!busy}
            onClick={() => run('test', () => api.post('/notification-settings/group/test'), 'Test message posted in the topic.')}>
            <Send size={14} /> {busy === 'test' ? 'Sending…' : 'Send test message'}
          </button>
          <button className="btn btn-secondary btn-sm" style={{ minHeight: '36px' }} disabled={!!busy}
            onClick={() => { if (window.confirm('Disconnect the group? The bot stops posting there.')) run('disconnect', () => api.post('/notification-settings/group/disconnect'), 'Group disconnected.'); }}>
            <Unplug size={14} /> Disconnect
          </button>
        </div>
      ) : (
        <div style={{ marginBottom: '12px', padding: '10px 12px', borderRadius: 'var(--radius)', background: 'var(--bg)', border: '1px solid var(--border)', fontSize: '0.85rem', lineHeight: 1.55 }}>
          <strong>Not connected.</strong> To connect: add the bot{data.bot_username ? ` @${data.bot_username}` : ''} to the staff group,
          then send <code>/connect</code> inside the &quot;Shift Exchange&quot; topic — from your own Telegram, linked to your
          admin account (Profile → Telegram). Then reload this page.
        </div>
      )}

      {g.last_error && (
        <div role="alert" style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', marginBottom: '12px', padding: '8px 10px', borderRadius: 'var(--radius)', background: 'var(--danger-light)', border: '1px solid var(--danger)', fontSize: '0.82rem' }}>
          <AlertTriangle size={15} style={{ flexShrink: 0, marginTop: '2px', color: 'var(--danger)' }} />
          <span><strong>Last problem ({fmt(g.last_error_at)}):</strong> {g.last_error}</span>
        </div>
      )}
      {!g.last_error && g.last_ok_at && (
        <p style={{ margin: '0 0 12px', fontSize: '0.8rem', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: '6px' }}>
          <CheckCircle2 size={14} style={{ color: 'var(--success)' }} /> Last post: {fmt(g.last_ok_at)}
        </p>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', borderTop: '1px solid var(--border)' }}>
        <PostRow title="Weekly preview" text="Open shifts of the coming Mon–Sun.">
          <Toggle checked={posts.weekly.on} onChange={(v) => set('weekly', { on: v })} label="On" />
          <select className="form-select" value={posts.weekly.day} onChange={(e) => set('weekly', { day: Number(e.target.value) })}
            style={{ minHeight: '36px', padding: '4px 8px', width: 'auto', fontSize: '0.85rem' }} aria-label="Weekday of the weekly preview">
            {WEEKDAYS.map((d, i) => <option key={d} value={i}>{d}</option>)}
          </select>
          <input type="time" className="form-input" value={posts.weekly.time} onChange={(e) => set('weekly', { time: e.target.value })}
            style={{ minHeight: '36px', width: '110px', padding: '4px 8px' }} aria-label="Time of the weekly preview" />
        </PostRow>
        <PostRow title="Swap wanted" text="Right away when someone asks for a swap in the app (00:00–07:00 held until 07:00).">
          <Toggle checked={posts.swap.on} onChange={(v) => set('swap', { on: v })} label="On" />
        </PostRow>
        <PostRow title="Day before" text="Tomorrow's team and the shifts still open. Later freed shifts follow in one more post.">
          <Toggle checked={posts.day_before.on} onChange={(v) => set('day_before', { on: v })} label="On" />
          <input type="time" className="form-input" value={posts.day_before.time} onChange={(e) => set('day_before', { time: e.target.value })}
            style={{ minHeight: '36px', width: '110px', padding: '4px 8px' }} aria-label="Time of the day-before post" />
        </PostRow>
        <PostRow title="Same day" text="A shift freed on its own day (00:00–07:00 held until 07:00); repeated at most every 3 hours while still open, until it starts.">
          <Toggle checked={posts.same_day.on} onChange={(v) => set('same_day', { on: v })} label="On" />
        </PostRow>
      </div>
      <p style={{ margin: '8px 0 0', fontSize: '0.78rem', color: 'var(--text-muted)' }}>
        Times are Berlin time. Switching a post type on only covers requests from then on.
      </p>
      <div style={{ display: 'flex', gap: '8px', marginTop: '12px', flexWrap: 'wrap' }}>
        <button className="btn btn-primary btn-sm" style={{ minHeight: '40px' }} disabled={!changed || !!busy}
          onClick={() => run('save', () => api.put('/notification-settings/group/posts', { posts }), 'Group posts saved.')}>
          {busy === 'save' ? 'Saving…' : 'Save'}
        </button>
        {changed && (
          <button className="btn btn-secondary btn-sm" style={{ minHeight: '40px' }} onClick={() => setPosts(g.posts)} disabled={!!busy}>Cancel</button>
        )}
      </div>
    </div>
  );
}

function PostRow({ title, text, children }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '4px 12px', padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
      <div style={{ flex: '1 1 240px', minWidth: 0 }}>
        <div style={{ fontSize: '0.88rem', fontWeight: 600 }}>{title}</div>
        <div style={{ fontSize: '0.76rem', color: 'var(--text-muted)' }}>{text}</div>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px' }}>{children}</div>
    </div>
  );
}

export default function NotificationSettings() {
  const [data, setData] = useState(null);
  const load = useCallback(() => {
    api.get('/notification-settings').then((r) => setData(r.data)).catch(() => toast.error('Failed to load the notification settings.'));
  }, []);
  useEffect(() => { load(); }, [load]);
  if (!data) return null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      <ChannelsCard data={data} onSaved={load} />
      <GroupCard data={data} onSaved={load} />
    </div>
  );
}
